import React from 'react';
import { StyleSheet } from 'react-native';
import { useForm } from 'react-hook-form';
import { DNABox, DNAButton, DNAButtonProps, DNAText, DNAContextMenu, Iffy } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { DNAConditionalButton, HelpCircleTooltip } from 'src/components/DNA';
import DNACommonConfirmation from 'src/components/DNA/Modal/DNACommonConfirmation';
import useMachineSelector, { composite } from 'src/hooks/useSelector';
import * as publisherVersioningSelector from 'src/state/machines/publisherVersioning/publisherVersioning.selectors';
import { useDispatch } from 'src/state/redux';
import { usePublisherVersioningState }
  from 'src/state/machines/publisherVersioning/PublisherVersioningProvider';
import { useSlideSettingsState } from 'src/state/machines/publisherVersioning/SlideSettings/SlideSettingsProvider';
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal';

export const styles = StyleSheet.create({
  actionBar: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomColor: colors['color-gray-80'],
    borderBottomWidth: 1,
  },
  helpCircleTooltip: {
    marginLeft: 5,
  },
});

const buttonDefaults: DNAButtonProps = {
  appearance:'outline',
  status:'tertiary',
  size: 'sm',
}

const Header: React.FC = () => {
  const {
    thumbnailSizes,
    currentDocumentVersionORM,
    pages,
    setCoverThumbnail,
    editRequiredSlides,
    addAssociatedSlides,
    removeAssociatedSlides,
    editGroupSlides,
  } = useSlideSettingsState()

  const form = useForm({ mode:'onSubmit' })
  const { service: parentService, latestContentPageData } = usePublisherVersioningState();
  const dispatch = useDispatch()

  const hasAssociatedSlides = pages.some(page => page.linkedSlides?.length)
  const hasSlideTitlesToClear = !!latestContentPageData?.some(({ title }) => title !== '' )

  const handleClearAllSlideTitles = form.handleSubmit(() => {
    latestContentPageData?.forEach(page => (page.title = ''))
    parentService.send({ type: 'SET_IS_DIRTY', payload: { type: 'slidesData', isDirty: true } })
  })

  const handleLaunchClearAllSlideTitlesModal = () => latestContentPageData &&
    dispatch(DNAModalActions.setModal({
      isVisible: true,
      allowBackdropCancel: false,
      component: (props) =>
        (<DNACommonConfirmation
          { ...props }
          onConfirmAction={handleClearAllSlideTitles}
          confirmActionText="Clear"
          title="Clear all slide titles?"
          cancelText="Cancel"
        />),
    }))

  return (
    <DNABox spacing="between" style={styles.actionBar}>
      {/* left side */}
      <DNABox spacing="md" alignY="center">
        <DNAText>{currentDocumentVersionORM.meta.allPages.length} slide(s)</DNAText>
        <DNAButton
          {...buttonDefaults}
          onPress={setCoverThumbnail}
        >
          Set cover thumbnail
        </DNAButton>
        <DNAConditionalButton
          {...buttonDefaults}
          onPress={handleLaunchClearAllSlideTitlesModal}
        >
          <DNAConditionalButton.Disable condition={!hasSlideTitlesToClear}>
            <DNAText colorMode="dark">No slide titles to clear</DNAText>
          </DNAConditionalButton.Disable>
          Clear all slide titles
        </DNAConditionalButton>
      </DNABox>
      {/* right side */}
      <DNABox spacing="md">
        <HelpCircleTooltip
          popoverText="These settings apply when this document is used within a custom presentation."
          style={styles.helpCircleTooltip}
        />
        <DNAText>Custom Presentation Settings:</DNAText>
        <DNABox spacing="sm" alignY="center">
          <DNAContextMenu>
            <DNAContextMenu.Anchor>
              <DNAButton
                {...buttonDefaults}
                testID="edit-required-slides-dropdown"
                iconRight="caret-down"
              >
                Edit required slides
              </DNAButton>
            </DNAContextMenu.Anchor>
            <DNAContextMenu.Items>
              <DNAContextMenu.Item
                key="For-presentation"
                collapseOnPress
                delay={100}
                title="For presentation"
                onPress={editRequiredSlides}
              />
              <DNAContextMenu.Item
                key="Add-to-individual-slides"
                collapseOnPress
                delay={100}
                title="Add to individual slides"
                onPress={addAssociatedSlides}
              />
              { hasAssociatedSlides &&
                <DNAContextMenu.Item
                  key="Remove-from-individual-slides"
                  collapseOnPress
                  delay={100}
                  title="Remove from individual slides"
                  onPress={removeAssociatedSlides}
                />
            }
            </DNAContextMenu.Items>
          </DNAContextMenu>
          <DNAText>or</DNAText>
          <DNAButton
            {...buttonDefaults}
            testID="edit-slide-groups-btn"
            onPress={editGroupSlides}
          >
            Edit slide groups
          </DNAButton>
        </DNABox>
        <DNAButton
          {...buttonDefaults}
          iconLeft={thumbnailSizes.thumbnailSize === 'lg' ? 'grid-nine' : 'squares-four'}
          onPress={thumbnailSizes.cycleThumbnailSize}
        />
      </DNABox>
    </DNABox>
  )
}

const HeaderWrapper: React.FC = () => {
  const {
    service: parentService,
    isNonDocumentFile,
  } = usePublisherVersioningState();

  const versionPanelCond = useMachineSelector(
    parentService,
    (state) => composite(
      state,
      publisherVersioningSelector.isInDraftingState,
    ),
  )

  return (
    <Iffy is={versionPanelCond.isInDraftingState && !isNonDocumentFile}>
      <Header />
    </Iffy>
  )
}

export default HeaderWrapper;
